import React from 'react'
import {PathRouteProps} from 'react-router'
import {connect} from 'react-redux'
import {ApplicationState} from '../../redux/store.js'
import {User} from '../../types/user.js'
import Scaffold from '../Scaffold/Scaffold.js'
import {registerPasskey, supportsPasskeys} from './passkeys.js'
import passkeyProviderIcon from './passkeyProviderIcons.js'
import './PasskeySettings.css'

type Props = { user: User } & PathRouteProps
type Credential = { id: string, name: string, createdAt: string, lastUsedAt?: string | null }

class PasskeySettings extends React.Component<Props, {
    credentials: Credential[], error: string, busy: boolean, available: boolean,
    newPassword: string,
    passwordBusy: boolean, passwordError: string, passwordSuccess: string
}> {
    state = {
        credentials: [] as Credential[], error: '', busy: false, available: false,
        newPassword: '',
        passwordBusy: false, passwordError: '', passwordSuccess: ''
    }

    componentDidMount() { this.load() }

    async load() {
        try {
            const status = await fetch('/api/passkeys/status')
            if (!status.ok || !(await status.json()).available) {
                this.setState({available: false})
                return
            }
            this.setState({available: true})
            const response = await fetch('/api/passkeys')
            if (response.ok) this.setState({credentials: await response.json()})
            else this.setState({error: 'Could not load your passkeys.'})
        } catch { this.setState({error: 'Could not reach the passkey service.'}) }
    }

    async add() {
        this.setState({busy: true, error: ''})
        try {
            await registerPasskey()
            await this.load()
        } catch (error) {
            this.setState({error: error instanceof Error ? error.message : 'Could not add passkey.'})
        } finally { this.setState({busy: false}) }
    }

    async remove(id: string) {
        this.setState({busy: true, error: ''})
        try {
            const response = await fetch('/api/passkeys/remove', {
                method: 'POST', headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({id})
            })
            if (!response.ok) throw new Error('Could not remove passkey.')
            await this.load()
        } catch (error) {
            this.setState({error: error instanceof Error ? error.message : 'Could not remove passkey.'})
        } finally { this.setState({busy: false}) }
    }

    async changePassword(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault()
        this.setState({passwordError: '', passwordSuccess: ''})
        this.setState({passwordBusy: true})
        try {
            const response = await fetch('/api/account/password', {
                method: 'PUT', headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({newPassword: this.state.newPassword})
            })
            const result = await response.json().catch(() => ({}))
            if (!response.ok) throw new Error(result.message || 'Could not change password.')
            this.setState({newPassword: '', passwordSuccess: 'Password changed.'})
        } catch (error) {
            this.setState({passwordError: error instanceof Error ? error.message : 'Could not change password.'})
        } finally { this.setState({passwordBusy: false}) }
    }

    render() {
        return <Scaffold {...this.props}>
            <div className="passkey-settings">
                <header className="scaffold-header">Account settings</header>
                <section className="account-settings-section">
                    <h2>Password</h2>
                    <form className="account-settings-password-form" onSubmit={event => this.changePassword(event)}>
                        <input type="password" autoComplete="new-password" aria-label="New password"
                            placeholder="New password" required value={this.state.newPassword}
                            onChange={event => this.setState({newPassword: event.target.value})} />
                        <button type="submit" disabled={this.state.passwordBusy}>Change password</button>
                    </form>
                    {this.state.passwordError && <p role="alert">{this.state.passwordError}</p>}
                    {this.state.passwordSuccess && <p role="status">{this.state.passwordSuccess}</p>}
                </section>
                <section className="account-settings-section">
                    <h2>Passkeys</h2>
                    {!this.state.available && <p>Passkeys are not enabled on this server yet.</p>}
                    {this.state.available && <>
                        <p>Use a passkey saved in 1Password, your device, or another passkey manager to sign in.</p>
                        <div className="passkey-settings-actions">
                            <button type="button" disabled={!supportsPasskeys() || this.state.busy}
                                onClick={() => this.add()}>Add a passkey</button>
                        </div>
                        {this.state.error && <p role="alert">{this.state.error}</p>}
                        <ul>{this.state.credentials.map(credential => <li key={credential.id}>
                            <div className="passkey-settings-credential">
                                {passkeyProviderIcon(credential.name)
                                    ? <img className="passkey-settings-icon" src={passkeyProviderIcon(credential.name)} alt="" />
                                    : <svg className="passkey-settings-icon" viewBox="0 0 24 24" aria-hidden="true">
                                        <circle cx="8" cy="9" r="4" fill="none" stroke="currentColor" strokeWidth="2" />
                                        <path d="M11 12l9 9m-4-4l2-2m-1 5l2-2" fill="none" stroke="currentColor" strokeWidth="2" />
                                    </svg>}
                                <div className="passkey-settings-details">
                                    <strong>{credential.name}</strong>
                                    <small>Created: {new Date(credential.createdAt).toLocaleString()}</small>
                                    <small>Last used: {credential.lastUsedAt
                                        ? new Date(credential.lastUsedAt).toLocaleString() : 'Not yet used'}</small>
                                </div>
                            </div>
                            <button type="button" disabled={this.state.busy}
                                onClick={() => this.remove(credential.id)}>Remove</button>
                        </li>)}</ul>
                    </>}
                </section>
            </div>
        </Scaffold>
    }
}

export default connect(({userState}: ApplicationState) => ({user: userState.user}))(PasskeySettings)
